import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { I18n } from 'react-redux-i18n-lite';
import { SegmentedControl } from '@netexknowledge/smart-components';
import { getItemName, getSelectionKey, isSelectable } from './gradebookMobileUtils';
import { GRADEBOOK_SCOPE, TASK_TABS_CONSTANTS } from '../../../const';

// Lists longer than this get a search field
const SEARCH_MIN_ITEMS = 8;

const TASKS_FILTERS = ['IN_PROGRESS', 'FINISHED', 'ALL'];
const TASKS_FILTER_LABELS = { IN_PROGRESS: 'tasks.current', FINISHED: 'tasks.finished', ALL: 'tasks.all' };

const LEVELS = {
  book: { icon: 'reading-open-book', label: 'gradebook.book', placeholder: 'gradebook.select.books' },
  unit: {
    icon: 'directory',
    label: 'gradebook.unit',
    placeholder: 'gradebook.select.units',
    all: 'gradebook.mobile.allUnits'
  },
  lesson: {
    icon: 'blank-paper-sheet',
    label: 'gradebook.lesson',
    placeholder: 'gradebook.select.lessons',
    all: 'gradebook.mobile.allLessons'
  }
};

// Title of the sheet while a list is open
export const getFiltersViewTitle = view => I18n.t(LEVELS[view].placeholder);

/**
 * Body of the mobile gradebook filters sheet. `view` null shows the filters; 'book', 'unit' or 'lesson' shows
 * that list as the second view of the same sheet (the sheet header gets the back arrow from the filter bar).
 * Selecting calls the same actions as the desktop GradebookSidebar.
 */
export const GradebookFiltersSheetContent = props => {
  const {
    view,
    onViewChange,
    books,
    units,
    lessons,
    selectedBook,
    selectedUnit,
    selectedLesson,
    gradebookScope,
    setGradebookScope,
    tasksFilterParams,
    changeTasksFilterParams,
    routeParams,
    sectionId,
    classroomTabs
  } = props;
  const [query, setQuery] = useState('');
  const classroomId = routeParams.idClassroom;
  const isTasks = gradebookScope === GRADEBOOK_SCOPE.TASKS;

  const optionsByLevel = { book: books || [], unit: units || [], lesson: lessons || [] };
  const selectedByLevel = { book: selectedBook, unit: selectedUnit, lesson: selectedLesson };

  const select = (level, item) => {
    if (level === 'book') {
      props.setBookData(classroomId, item, sectionId);
    } else if (level === 'unit') {
      props.setUnitData(item, sectionId, selectedBook, classroomId);
    } else {
      props.setLessonData(item, sectionId, selectedUnit, classroomId);
    }
    setQuery('');
    onViewChange(null);
  };

  if (view) {
    const level = LEVELS[view];
    const options = optionsByLevel[view].filter(isSelectable);
    const selectedKey = getSelectionKey(selectedByLevel[view]);
    const normalizedQuery = query.trim().toLowerCase();
    const visible = normalizedQuery
      ? options.filter(item =>
          getItemName(item)
            .toLowerCase()
            .includes(normalizedQuery)
        )
      : options;

    const renderOption = (key, name, item, active) => (
      <li key={key}>
        <button
          type="button"
          className={active ? 'gradebook-filters-option active' : 'gradebook-filters-option'}
          aria-pressed={active}
          onClick={() => select(view, item)}
        >
          <i className={`custom-icons-${level.icon} gradebook-filters-option-icon`} aria-hidden="true" />
          <span className="gradebook-filters-option-name">{name}</span>
          {active && <i className="custom-icons-check-icon gradebook-filters-option-check" aria-hidden="true" />}
        </button>
      </li>
    );

    return (
      <div className="gradebook-sheet-filters list-view">
        {options.length > SEARCH_MIN_ITEMS && (
          <div className="gradebook-filters-search">
            <i className="custom-icons-magnifying-glass-browser" aria-hidden="true" />
            <input
              type="search"
              value={query}
              placeholder={I18n.t('common.search')}
              aria-label={I18n.t('common.search')}
              onChange={event => setQuery(event.target.value)}
            />
          </div>
        )}
        <ul className="gradebook-filters-options">
          {level.all && !normalizedQuery && renderOption('all', I18n.t(level.all), undefined, !selectedKey)}
          {visible.map(item =>
            renderOption(getSelectionKey(item), getItemName(item), item, getSelectionKey(item) === selectedKey)
          )}
        </ul>
      </div>
    );
  }

  const hasTasksScope = (classroomTabs || []).includes('ASSIGNMENTS');
  const contentRow = levelKey => {
    const level = LEVELS[levelKey];
    const disabled = optionsByLevel[levelKey].length === 0;
    const selected = selectedByLevel[levelKey];
    return (
      <button
        type="button"
        className="gradebook-filters-level"
        disabled={disabled}
        aria-haspopup="true"
        onClick={() => onViewChange(levelKey)}
      >
        <i className={`custom-icons-${level.icon} gradebook-filters-level-icon`} aria-hidden="true" />
        <span className="gradebook-filters-level-text">
          <span className="gradebook-filters-level-label">{I18n.t(level.label)}</span>
          <span className="gradebook-filters-level-value">
            {selected ? getItemName(selected) : I18n.t(level.placeholder)}
          </span>
        </span>
        <i className="custom-icons-right-chevron gradebook-filters-level-chevron" aria-hidden="true" />
      </button>
    );
  };

  let unitLessonsLabel = 'gradebook.scope.unit_lessons';
  if (selectedBook && !selectedUnit) unitLessonsLabel = 'gradebook.scope.units';
  if (selectedUnit && !selectedLesson) unitLessonsLabel = 'gradebook.scope.lessons';

  const tasksFilterKey =
    (tasksFilterParams && tasksFilterParams.status && tasksFilterParams.status.key) || 'IN_PROGRESS';

  return (
    <div className="gradebook-sheet-filters">
      {hasTasksScope && (
        <section className="gradebook-filters-section">
          <h3 className="gradebook-filters-title">{I18n.t('common.option.view')}</h3>
          <SegmentedControl
            variant="filled"
            label={I18n.t('common.option.view')}
            segments={[
              { key: 'TASKS', label: I18n.t('gradebook.scope.tasks') },
              { key: 'CONTENT', label: I18n.t('gradebook.scope.content') }
            ]}
            activeKey={isTasks ? 'TASKS' : 'CONTENT'}
            onChange={key => setGradebookScope(key === 'TASKS' ? GRADEBOOK_SCOPE.TASKS : GRADEBOOK_SCOPE.ACTIVITIES)}
          />
        </section>
      )}

      {isTasks ? (
        <section className="gradebook-filters-section">
          <h3 className="gradebook-filters-title">{I18n.t('common.appliedFilters').replace(/:\s*$/, '')}</h3>
          <SegmentedControl
            variant="filled"
            label={I18n.t('common.appliedFilters')}
            segments={TASKS_FILTERS.map(key => ({ key, label: I18n.t(TASKS_FILTER_LABELS[key]) }))}
            activeKey={tasksFilterKey}
            onChange={key =>
              changeTasksFilterParams(classroomId, key === 'ALL' ? {} : { status: TASK_TABS_CONSTANTS[key] })
            }
          />
        </section>
      ) : (
        <>
          <section className="gradebook-filters-section">
            <h3 className="gradebook-filters-title">{I18n.t('gradebook.select.title')}</h3>
            <div className="gradebook-filters-levels">
              {contentRow('book')}
              {contentRow('unit')}
              {contentRow('lesson')}
            </div>
          </section>
          <section className="gradebook-filters-section">
            <h3 className="gradebook-filters-title">{I18n.t('gradebook.scope.title')}</h3>
            <SegmentedControl
              variant="filled"
              label={I18n.t('gradebook.scope.title')}
              segments={[
                { key: 'ACTIVITIES', label: I18n.t('gradebook.scope.activities') },
                ...(selectedLesson ? [] : [{ key: 'UNIT_LESSONS', label: I18n.t(unitLessonsLabel) }])
              ]}
              activeKey={gradebookScope === GRADEBOOK_SCOPE.UNIT_LESSONS ? 'UNIT_LESSONS' : 'ACTIVITIES'}
              onChange={key => setGradebookScope(GRADEBOOK_SCOPE[key])}
            />
          </section>
        </>
      )}
    </div>
  );
};

GradebookFiltersSheetContent.propTypes = {
  view: PropTypes.oneOf(['book', 'unit', 'lesson']),
  onViewChange: PropTypes.func.isRequired,
  books: PropTypes.array,
  units: PropTypes.array,
  lessons: PropTypes.array,
  selectedBook: PropTypes.object,
  selectedUnit: PropTypes.object,
  selectedLesson: PropTypes.object,
  gradebookScope: PropTypes.symbol,
  setGradebookScope: PropTypes.func,
  tasksFilterParams: PropTypes.object,
  changeTasksFilterParams: PropTypes.func,
  routeParams: PropTypes.any,
  sectionId: PropTypes.string,
  setBookData: PropTypes.func,
  setUnitData: PropTypes.func,
  setLessonData: PropTypes.func,
  classroomTabs: PropTypes.array
};

const mapStateToProps = state => ({
  classroomTabs: state.account.configuration.classroomTabs
});

export default connect(mapStateToProps)(GradebookFiltersSheetContent);
